// What the PAGE looks like while a focused session is up (busy-my-web#255).
//
// `isFocusedSession` (@/lib/creationCapabilities) answers WHETHER the page is emptied; this file
// answers WHAT is left behind on the two rails that do not disappear:
//
//   · the Ongoing list keeps ONE row — the ongoing being worked in, or the entity open in the box
//     (frames 40008539-200567, 40008716-284181, 40008863-378008, 40008810-369754, 40008931-392836);
//   · the Timeline keeps ONE item — "Ready when you are" over "<Module> opened", the creation
//     session's own row (reporter's second thread reply on #255).
//
// Plain functions over the plain session shape, for the same reason the gate next door is one: the
// rule is then testable without mounting the sidebar or the rail, and the two surfaces read the
// same sentence rather than each carrying a copy of it.

import { moduleColor, moduleKey } from '@/lib/moduleColors'
import { moduleIcon } from '@/lib/moduleIcons'
import { isCreationSession, isFocusedSession } from '@/lib/creationCapabilities'
import { selectedFileRows, selectionCountLine } from '@/lib/selectedFiles'

/** The rail's title over the one focused item — exactly the frames' wording. */
export const READY_TITLE = 'Ready when you are'

// The description under it, per module. "contact opened" USED to be lower-case on purpose (it was
// how the reporter wrote the set out — "Task opened — Email opened — Event opened — Note opened —
// contact opened" — and no creation frame contradicted her). The contact-creation frame does now:
// 40002456:48825 writes "Contact opened" under "Ready when you are", capitalised like the other four
// (2026-09-15 parity audit C-10, busy-my-web#475), so the set is one spelling again.
const OPENED_LABEL = {
  task: 'Task opened',
  email: 'Email opened',
  event: 'Event opened',
  note: 'Note opened',
  contact: 'Contact opened',
}

// What the Ongoing list's child line is waiting FOR — the same vocabulary the sidebar already uses
// for a drafted search ("Waiting for your search…").
const WAITING_FOR = {
  task: 'task',
  email: 'email',
  event: 'event',
  note: 'note',
  contact: 'contact',
  research: 'search',
}

// A module's name as a row title when the thing has none yet ("New Ongoing" covers the draft case).
const MODULE_LABEL = {
  task: 'Task',
  email: 'Email',
  event: 'Event',
  note: 'Note',
  contact: 'Contact',
  research: 'Research',
}

/**
 * The module the focused session is about — canonical ('task' | 'email' | …), '' when none.
 *
 * The entity being EDITED wins over the box's mode, because opening one sets both (the box switches
 * to the entity's own menu to show it), and it is the entity that names the row.
 */
export function focusedModule(session) {
  const s = session || {}
  const from = s.editing?.menu || s.quickMode || (s.formMode ? s.formMenu : '') || ''
  return moduleKey(from)
}

/**
 * The Ongoing draft row's own title. "New Ongoing" until a title is typed, which then replaces it —
 * except for a NOTE (Figma 40008598:241165, busy-my-web#651): every note frame, filled ones included,
 * keeps "New Ongoing" over "Waiting for your note…" while the note's title is "Bank details
 * extracted". Figma wins over the replace-with-title rule for that kind only.
 */
export function entityDraftTitle(kind, typed) {
  const t = String(typed || '').trim()
  if (!t || moduleKey(kind) === 'note') return 'New Ongoing'
  return t
}

/** "Waiting for your task…" — the Ongoing draft row's child line for this session's module. */
export function waitingLine(module) {
  const key = moduleKey(module)
  return key ? `Waiting for your ${WAITING_FOR[key] || key}…` : ''
}

/**
 * The ONE Timeline item a creation session puts on the rail, or null.
 *
 * Null while EDITING: the frames give an edit the entity's own timeline ("Task timeline"), not this
 * invitation — the entity has a history and the rail shows it. Null for a research pack too: the
 * search already owns that row ("Search opened / Ready when you are", lib/researchTimeline.js), and
 * two rails saying the same thing in the same place is how #157 happened.
 */
export function focusedTimelineItem(session, files) {
  // A reply keeps the thread's own Email timeline on the rail (busy-my-web#480, frame
  // 40000017:13522) — no "Ready when you are / Email opened" row for it.
  if (!isCreationSession(session) || session?.editing || session?.replying) return null
  const module = focusedModule(session)
  if (!module || !OPENED_LABEL[module]) return null
  // What has been ATTACHED to the thing being made (busy-my-web#444, frame 40008539:201115): the
  // item lists the selected files and says how many there are, in place of "Task opened". With
  // nothing selected the recorded wording stands unchanged — the description only becomes a count
  // once there is something to count, so the empty state is exactly what #255 pinned.
  const rows = selectedFileRows(files)
  const counted = selectionCountLine(rows.length)
  const item = {
    module,
    color: moduleColor(module),
    icon: moduleIcon(module),
    title: READY_TITLE,
    description: counted || OPENED_LABEL[module],
  }
  // The list JOINS the item only once there is something in it: with nothing attached the item is
  // the one #255 pinned, key for key, and the rail draws the same two lines it always did.
  if (rows.length) item.files = rows
  return item
}

/**
 * The single Ongoing row for the entity open in the box, when the list holds no row of its own for
 * it (a standalone task, a contact — neither of which is an ongoing). Null outside an edit.
 */
export function focusedEntityRow(session) {
  const editing = session?.editing
  if (!editing) return null
  const module = focusedModule(session)
  if (!module) return null
  return {
    id: editing.ongoingId || null,
    icon: moduleIcon(module),
    iconColor: moduleColor(module),
    title: String(editing.title || '').trim() || MODULE_LABEL[module] || 'Ongoing',
    status: '',
    statusColor: '',
    child: false,
  }
}

/**
 * The Ongoing list, trimmed for a focused session: only the ongoing being worked in.
 *
 * Outside a focused session the list is returned UNTOUCHED (the same array), so nothing changes for
 * the ordering, the hoist and the sub-story nesting this list is otherwise about.
 */
export function focusedOngoingRows(rows, session) {
  const all = rows || []
  if (!isFocusedSession(session)) return all
  const id = session?.editing?.ongoingId || null
  const kept = all.filter(r => r && (r.active || (id && r.id === id)))
  if (kept.length) return kept
  const own = focusedEntityRow(session)
  return own ? [own] : []
}

// ── THE READER TRIMS THE LIST TO ITS OWN ONGOINGS (busy-my-web#617) ───────────────────────────
//
// Pouria, 2026-10-09: "When we are in a task or note or event in their module, we know those relate
// to an Ongoing; in the left-hand side we need to just filter Ongoings and show the related Ongoing
// to the task or note."
//
// This is the ONE place a reader trims the left list. It is gated on the READER ROUTE
// (`/tasks/item/:id`, `/notes/item/:id`, `/events/item/:id` — lib/entityReader.js) together with
// an opened item, never on `focusedSession.opened` alone: an inline expand in a listing (#483)
// publishes `opened` too, and the recorded decision there is that the page is NOT emptied for it.
// `isFocusedSession` is untouched — the reader is not a focused session either; it only narrows the
// list to the stories the open item belongs to (Ongoing –Contains→ its timeline).

/** The quiet line the list shows when the open item belongs to no Ongoing. */
export const NOT_IN_ONGOING_LINE = 'Not part of an ongoing yet'

/**
 * The Ongoing list while a reader is open: only the rows whose id is one of the open item's
 * Ongoings, in the order they arrived. `{ rows, empty }` — `empty` is true only when the rule
 * applied and nothing was left, which is when the surface draws `NOT_IN_ONGOING_LINE`.
 *
 * The list is returned UNTOUCHED (the same array, `empty: false`) when no reader is open, while the
 * edges are still being read (no flash to empty), and when they are unknown (`relatedIds` not an
 * array — a failed read must not claim the item is in no story).
 *
 * @param rows the list as the surface would draw it (FLAT: a related sub-story is its own row)
 * @param {{readerOpen?: boolean, relatedIds?: string[]|null, loading?: boolean}} opened
 */
export function openedOngoingRows(rows, { readerOpen = false, relatedIds = null, loading = false } = {}) {
  const all = rows || []
  if (!readerOpen || loading || !Array.isArray(relatedIds)) return { rows: all, empty: false }
  const wanted = new Set(relatedIds.map(String))
  const kept = all.filter(r => r && r.id && wanted.has(String(r.id)))
  return { rows: kept, empty: kept.length === 0 }
}

/**
 * Is the reader's trim in force for this path and session? A reader route (any of the three) with
 * an item opened on it, and the edges read for that item's timeline. The path test is the reader
 * module's own; nothing here repeats its patterns.
 *
 * @param isReaderPath `isEntityReaderPath(path)` — passed in so this file needs no route module
 * @param opened `focusedSession.opened`
 * @param openedOngoings `focusedSession.openedOngoings` — `{ timelineId, ids, loading }`
 */
export function openedReaderState(isReaderPath, opened, openedOngoings) {
  const o = openedOngoings || {}
  const readerOpen = !!isReaderPath && !!opened && !!o.timelineId
  return { readerOpen, relatedIds: readerOpen ? (o.ids ?? null) : null, loading: readerOpen && !!o.loading }
}

export default isFocusedSession
